import { supabase } from './lib/supabase';

// -------------------------------------------------------------------
// Types
// -------------------------------------------------------------------

interface PublishedProperty {
    id: string;
    slug: string;
    title: string;
    subtitle: string;
    thumbnail_url: string;
}

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------

function escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
}

// -------------------------------------------------------------------
// Fetch published properties (is_active = true)
// -------------------------------------------------------------------

async function fetchPublishedProperties(): Promise<PublishedProperty[]> {
    const { data, error } = await supabase
        .from('villa_properties')
        .select('id, slug, title, subtitle, thumbnail_url')
        .eq('is_active', true)
        .order('created_at', { ascending: true });

    if (error) {
        console.error('[Landing] Failed to fetch published properties:', error);
        return [];
    }
    return data || [];
}

// -------------------------------------------------------------------
// Render property cards
// -------------------------------------------------------------------

async function renderProperties(): Promise<void> {
    const grid = document.getElementById('properties-grid');
    if (!grid) return;

    const properties = await fetchPublishedProperties();

    // Hardcoded Diriyah card (custom build, not in CMS)
    const diriyahCard = `
        <a href="https://diriyahdemo.augmento.com" target="_blank" rel="noopener" class="property-card">
            <div class="property-thumbnail">
                <img src="https://res.cloudinary.com/dypt1fmsb/image/upload/v1771934859/gzkhmicouth0n9oqvv9h.png" alt="Diriyah">
            </div>
            <div class="property-body">
                <h3>Diriyah</h3>
                <p class="property-subtitle">Heritage Village Experience</p>
                <span class="property-view-link">
                    View Property <i class="hgi-stroke hgi-arrow-right-02"></i>
                </span>
            </div>
        </a>
    `;

    const dbCards = properties.map(prop => {
        const thumbnail = prop.thumbnail_url
            ? `<img src="${escapeHtml(prop.thumbnail_url)}" alt="${escapeHtml(prop.title)}" loading="lazy">`
            : `<div class="property-placeholder"><i class="hgi-stroke hgi-building-06"></i></div>`;

        return `
            <a href="/${escapeHtml(prop.slug)}" class="property-card">
                <div class="property-thumbnail">${thumbnail}</div>
                <div class="property-body">
                    <h3>${escapeHtml(prop.title)}</h3>
                    <p class="property-subtitle">${escapeHtml(prop.subtitle || '')}</p>
                    <span class="property-view-link">
                        View Property <i class="hgi-stroke hgi-arrow-right-02"></i>
                    </span>
                </div>
            </a>
        `;
    }).join('');

    grid.innerHTML = diriyahCard + dbCards;
}

// -------------------------------------------------------------------
// Demo form handler
// -------------------------------------------------------------------

function setupDemoForm(): void {
    const form = document.getElementById('demo-form') as HTMLFormElement | null;
    if (!form) return;

    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitBtn = form.querySelector('.demo-submit') as HTMLButtonElement;
        const nameEl = document.getElementById('demo-name') as HTMLInputElement;
        const emailEl = document.getElementById('demo-email') as HTMLInputElement;
        const companyEl = document.getElementById('demo-company') as HTMLInputElement;
        const messageEl = document.getElementById('demo-message') as HTMLTextAreaElement;

        const name = nameEl.value.trim();
        const email = emailEl.value.trim();
        const company = companyEl.value.trim();
        const message = messageEl.value.trim();

        if (!name || !email) return;

        // Show loading state
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span>Sending...</span>';

        try {
            const { error } = await supabase
                .from('demo_requests')
                .insert({ name, email, company: company || null, message: message || null });

            if (error) throw error;

            // Show success state
            form.innerHTML = `
                <div class="demo-success">
                    <h3>Thank you!</h3>
                    <p>We'll be in touch shortly.</p>
                </div>
            `;
        } catch (err) {
            console.error('[Demo] Form submission error:', err);
            submitBtn.disabled = false;
            submitBtn.innerHTML = '<span>Send Request</span><i class="hgi-stroke hgi-arrow-right-02"></i>';
            alert('Something went wrong. Please try again.');
        }
    });
}

// -------------------------------------------------------------------
// Smooth scroll for anchor links
// -------------------------------------------------------------------

function setupSmoothScroll(): void {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
            const href = (anchor as HTMLAnchorElement).getAttribute('href');
            if (!href || href === '#') return;

            const target = document.querySelector(href);
            if (target) {
                e.preventDefault();
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    });
}

// -------------------------------------------------------------------
// Init
// -------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', () => {
    renderProperties();
    setupDemoForm();
    setupSmoothScroll();
});
